import { Box, Button, Paper, Stack, Typography, MenuItem } from '@mui/material';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import MonacoYamlEditor from 'src/components/MonacoYamlEditor';
import { addTankResource, getTankCategories, getTankResourceDetail, updateTankResource } from 'src/api';
import message from 'src/components/message';
import yaml from 'js-yaml';

// 一级分类数据接口
interface TankCategory {
  id: string;
  name: string;
}

export default function TankTypeAddSecond() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editKey = searchParams.get('key'); // 获取编辑的key
  const isEditMode = !!editKey; // 是否为编辑模式
  
  const [loading, setLoading] = useState(false);
  const [yamlValue, setYamlValue] = useState('');
  const [parentTypeOptions, setParentTypeOptions] = useState<TankCategory[]>([]);
  const [editData, setEditData] = useState<any>(null);

  const defaultValues = {
    name: '',
    parentId: '',
  };
  const methods = useForm({ defaultValues });
  const { handleSubmit, reset, setValue } = methods;

  // 获取一级分类数据
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response: any = await getTankCategories();
        console.log('getTankCategories response:', response); // 调试日志
        if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
          setParentTypeOptions(response.data.data);
        } else {
          console.error('一级分类数据格式不正确:', response);
          setParentTypeOptions([]);
        }
      } catch (error) {
        console.error('获取一级分类失败:', error);
        setParentTypeOptions([]);
      }
    };
    fetchCategories();
  }, []);

  // 如果是编辑模式，根据key获取数据
  useEffect(() => {
    if (isEditMode && editKey && parentTypeOptions.length > 0) {
      const fetchEditData = async () => {
        try {
          const response: any = await getTankResourceDetail(editKey);
          console.log('getTankResourceDetail response:', response);
          
          if (response && response.data) {
            // 详情接口返回单个对象，不是数组
            const item = response.data;
            setEditData(item);
            // 回显表单数据
            setValue('name', item.key || '');
            setValue('parentId', item.category.id || '');
            if (item.data) {
              setYamlValue(yaml.dump(item.data));
            } else {
              setYamlValue('');
            }
          } else {
            message.error({ content: '未找到对应的数据' });
            navigate(-1);
          }
        } catch (error) {
          console.error('获取编辑数据失败:', error);
          message.error({ content: '获取数据失败' });
          navigate(-1);
        }
      };
      fetchEditData();
    }
  }, [isEditMode, editKey, setValue, navigate, parentTypeOptions]);

  const onSubmit = async (data: any) => {
    if (!yamlValue.trim()) {
      message.error({ content: '请输入 YAML 配置内容' });
      return;
    }

    let jsonData: any;
    try {
      jsonData = yaml.load(yamlValue);
    } catch (e) {
      message.error({ content: 'YAML 格式错误，请检查' });
      return;
    }

    setLoading(true);
    try {
      if (isEditMode && editData) {
        // 编辑模式 - 更新现有数据
        const payload = {
          key: data.name,
          category_id: data.parentId,
          data: jsonData,
        };

        const response: any = await updateTankResource(editData.id, payload);
        if (response.code === 200) {
          message.success({ content: '二级分类更新成功！' });
          reset();
          setYamlValue('');
          navigate(-1);
        } else {
          message.error({ content: response.message });
        }
      } else {
        // 新增模式
        const payload = {
          key: data.name,
          category_id: data.parentId,
          data: jsonData,
        };

        const response: any = await addTankResource(payload);
        if (response.code === 200) {
          message.success({ content: '二级分类添加成功！' });
          reset();
          setYamlValue('');
          navigate(-1);
        } else {
          message.error({ content: response.message });
        }
      }
      
      
    } catch (error) {
      console.error(`${isEditMode ? '更新' : '添加'}二级分类失败:`, error);
      message.error({ content: `${isEditMode ? '更新' : '添加'}二级分类失败，请重试` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {isEditMode ? '编辑二级分类' : '添加二级分类'}
        </Typography>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <RHFTextField
              name="parentId"
              label="所属一级分类"
              select
              required
            >
              <MenuItem value="">请选择</MenuItem>
              {Array.isArray(parentTypeOptions) && parentTypeOptions.map(category => (
                <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
              ))}
            </RHFTextField>
            
            <RHFTextField
              name="name"
              label="资源KEY"
              placeholder="请输入资源KEY(不能为中文，且不能重复)"
              required
              disabled={isEditMode}
            />
            
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                YAML 配置内容 *
              </Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  minHeight: 350, 
                  p: 0, 
                  borderRadius: 2, 
                  overflow: 'hidden', 
                  bgcolor: 'background.paper',
                  boxShadow: '0 1px 4px 0 rgba(0,0,0,0.04)',
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: '0 4px 16px 0 rgba(0,0,0,0.10)',
                  },
                  mb: 2,
                }}
              >
                <MonacoYamlEditor
                  height="350px"
                  value={yamlValue}
                  onChange={(value: string | undefined) => setYamlValue(value || '')}
                  placeholder="# 在此编写您的 YAML 配置"
                />
              </Paper>
            </Box>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => navigate(-1)}>
                取消
              </Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? '提交中...' : isEditMode ? '确认更新' : '确认添加'}
              </Button>
            </Stack>
          </Stack>
        </FormProvider>
      </Paper>
    </Box>
  );
}